/** @format */

import React, { type Ref } from 'react'

const cx = (...classes: (string | boolean | undefined | null)[]) =>
  classes.filter(Boolean).join(' ')

const ShortcutContainer = ({
  $collapsed,
  width,
  $isMini,
  $menu,
  $menuMini,
  height,
  $bgColor,
  $isOnProcess,
  theme,
  className,
  style,
  children,
  onMouseEnter,
  onMouseLeave,
  ref,
  ...props
}: any & { ref?: Ref<any> }) => {
  const isMiniOrCollapsed = $isMini || $collapsed === true
  const resolvedWidth = $collapsed === true ? '60px' : width || ($isMini ? '113px' : '230px')
  const resolvedHeight = height || '120px'
  return (
    <div
      ref={ref}
      className={cx(
        'relative overflow-hidden rounded-none flex flex-col gap-[5px] transition-all duration-500',
        className,
      )}
      style={{
        width: resolvedWidth,
        minWidth: resolvedWidth,
        maxWidth: resolvedWidth,
        height: resolvedHeight,
        backgroundColor: $isOnProcess
          ? 'rgba(255, 255, 255, 0.10)'
          : $bgColor || 'var(--shortcut-active-bg)',
        alignItems: isMiniOrCollapsed ? 'center' : 'flex-start',
        justifyContent: isMiniOrCollapsed ? 'center' : 'flex-start',
        border: $isOnProcess ? '1px solid #eeeeee' : '1px solid #e9e9e9',
        boxShadow: 'none',
        cursor: !$isOnProcess ? 'pointer' : 'default',
        backdropFilter: $isOnProcess && $isMini ? 'none' : 'blur(32px)',
        WebkitBackdropFilter: $isOnProcess && $isMini ? 'none' : 'blur(32px)',
        opacity: 1,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!$isOnProcess) {
          e.currentTarget.style.opacity = '0.8'
          e.currentTarget.style.backdropFilter = 'unset'
          e.currentTarget.style.setProperty('-webkit-backdrop-filter', 'unset')
        }
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1'
        e.currentTarget.style.backdropFilter = 'blur(32px)'
        e.currentTarget.style.setProperty('-webkit-backdrop-filter', 'blur(32px)')
        onMouseLeave?.(e)
      }}
      {...props}
    >
      {children}
    </div>
  )
}
ShortcutContainer.displayName = 'ShortcutContainer'

const Badge = ({
  $badgeColor,
  $isMini,
  $menu,
  className,
  style,
  children,
  ref,
  ...props
}: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx(
      'text-white text-xs font-semibold rounded-full w-7 h-7 leading-[14px] flex items-center justify-center transition-transform duration-200',
      className,
    )}
    style={{
      backgroundColor: $badgeColor || '#fbb034',
      margin: !$isMini ? '0 0 0 20px' : '0px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
Badge.displayName = 'Badge'

const Title = ({
  $isMini,
  $menuMini,
  $menu,
  $length,
  $textColor,
  $bgColor,
  theme,
  className,
  style,
  children,
  ref,
  ...props
}: any & { ref?: Ref<any> }) => {
  const getMargin = () => {
    if ($isMini && !$menuMini) return '0px 15px'
    if ($menu) return '8px'
    if ($menuMini) return '0 8px'
    return '20px 0 0 20px'
  }

  return (
    <div
      ref={ref}
      className={cx('font-[Roboto,sans-serif] transition-transform duration-200', className)}
      style={{
        margin: getMargin(),
        textAlign: $menu || $menuMini || $isMini ? 'center' : 'left',
        fontSize: $length < 3 ? '32px' : $menu || $menuMini ? '12px' : '14px',
        fontStyle: 'normal',
        fontWeight: $length < 3 ? '700' : '400',
        lineHeight: $length < 3 ? 'normal' : '14px',
        color: $textColor || 'var(--shortcut-active-text)',
        letterSpacing: $length < 3 ? '-0.96px' : 'normal',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  )
}
Title.displayName = 'Title'

const IconWrapper = ({
  $isMini,
  $collapsed,
  $menu,
  theme,
  width: w,
  height: h,
  className,
  style,
  children,
  ref,
  ...props
}: any & { ref?: Ref<any> }) => {
  const isAbsolute = !$isMini && !$collapsed && !$menu
  return (
    <div
      ref={ref}
      className={cx(
        'flex items-center justify-center transition-transform duration-200',
        className,
      )}
      style={{
        position: isAbsolute ? 'absolute' : 'relative',
        bottom: isAbsolute ? '8px' : 'unset',
        right: isAbsolute ? '8px' : 'unset',
        width: w || undefined,
        height: h || undefined,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  )
}
IconWrapper.displayName = 'IconWrapper'

export { ShortcutContainer, Badge, Title, IconWrapper }
